import { useCallback, useEffect, useMemo, useState } from 'react'

import { BundleStickers } from '@/components/stickers/bundle-stickers'
import { IdStickers } from '@/components/stickers/id-stickers'
import { PackingListStickers } from '@/components/stickers/packing-list-stickers'
import { ShippingStickers } from '@/components/stickers/shipping-stickers'
import { SkidStickers } from '@/components/stickers/skid-stickers'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { usePrintStickers } from '@/store/print-stickers'

const stickerLabels = ['id', 'bundle', 'shipping', 'skid', 'packing'] as const
export type StickerLabel = (typeof stickerLabels)[number]

export const StickersPopup = () => {
  const { printDetail, setPrintDetail, printOrder, setPrintOrder, printSkid, setPrintSkid } =
    usePrintStickers()

  const [open, setOpen] = useState(false)
  const [selectedTab, setSelectedTab] = useState<StickerLabel>('shipping')

  useEffect(() => {
    if (printDetail) {
      setSelectedTab(printDetail.labels?.[0] ?? 'id')
      setOpen(true)
    } else if (printOrder) {
      setSelectedTab(printOrder.labels?.[0] ?? 'shipping')
      setOpen(true)
    } else if (printSkid) {
      setSelectedTab('skid')
      setOpen(true)
    } else {
      setOpen(false)
    }
  }, [printDetail, printOrder, printSkid])

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        setPrintDetail(null)
        setPrintOrder(null)
        setPrintSkid(null)
      }
      setOpen(newOpen)
    },
    [setPrintDetail, setPrintOrder, setPrintSkid]
  )

  const availableTabs = useMemo(() => {
    const tabs: { value: StickerLabel; label: string }[] = []
    if (printDetail?.labels.includes('id')) {
      tabs.push({ value: 'id', label: 'Identification Labels' })
    }
    if (printDetail?.labels?.includes('bundle')) {
      tabs.push({ value: 'bundle', label: 'Bundle Labels' })
    }
    if (printOrder?.labels?.includes('shipping')) {
      tabs.push({ value: 'shipping', label: 'Shipping Labels' })
    }
    if (printOrder?.labels?.includes('packing')) {
      tabs.push({ value: 'packing', label: 'Packing List' })
    }
    if (printSkid) {
      tabs.push({ value: 'skid', label: 'Shipping Unit Label' })
    }
    return tabs
  }, [printDetail, printOrder, printSkid])

  const renderStickerContent = useMemo(() => {
    switch (selectedTab) {
      case 'id':
        return printDetail?.detail ? (
          <IdStickers
            detail={printDetail.detail}
            inven={printDetail.inven}
          />
        ) : null
      case 'bundle':
        return printDetail?.detail ? <BundleStickers detail={printDetail.detail} /> : null
      case 'shipping':
        return printOrder?.order ? (
          <ShippingStickers
            order={printOrder.order}
            skidId={printOrder.skidId}
            skidLabel={printOrder.skidLabel}
            skidSize={printOrder.skidSize}
            skidWeight={printOrder.skidWeight}
            skidIndex={printOrder.skidIndex}
            skidTotal={printOrder.skidTotal}
            skids={printOrder.skids}
          />
        ) : null
      case 'packing':
        return printOrder?.order ? <PackingListStickers order={printOrder.order} /> : null
      case 'skid':
        return printSkid ? (
          <SkidStickers
            skidId={printSkid.skidId}
            skidLabel={printSkid.skidLabel}
            skidType={printSkid.skidType}
          />
        ) : null
      default:
        return null
    }
  }, [selectedTab, printDetail, printOrder, printSkid])

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
    >
      <DialogTrigger className='hidden' />
      {/* `!max-w-235` let the 6in/4in sticker pages push the dialog past a phone viewport. */}
      <DialogContent className='max-h-[90vh] min-h-132 max-w-[calc(100%-2rem)] overflow-auto sm:!max-w-235'>
        <DialogHeader>
          <DialogTitle className='leading-none'>Print Stickers</DialogTitle>
        </DialogHeader>

        <Tabs
          onValueChange={(value) => setSelectedTab(value as StickerLabel)}
          value={selectedTab}
        >
          <TabsList className='w-full'>
            {availableTabs.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent
            className='mt-3.5 flex items-start justify-center'
            value={selectedTab}
          >
            {renderStickerContent}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
